import React, { useContext, type ReactNode } from 'react';
import { useABTest } from '../../extensible/ab-test-context';
import { useJiraCurrentUser, useJiraSearchClientContext } from '../clients';
import { BROCCOLI_EXPERIMENTS } from '../filter-context';

export const JIRA_DATE_FILTERS_EXPERIMENT_ID = 'broccoli';
export interface JiraFeatures {
	hasSoftwareAccess: boolean;
	hasAdvancedRoadmapsAccess?: boolean;
	isMultiSite?: boolean;
	enableDateFromFilter?: boolean;
	defaultDateFromFilter?: number;
}

export const DefaultFeatures: JiraFeatures = Object.freeze({
	hasSoftwareAccess: false,
	hasAdvancedRoadmapsAccess: false,
	isMultiSite: false,
	enableDateFromFilter: false,
	defaultDateFromFilter: undefined,
});

const JiraFeatureContext = React.createContext<JiraFeatures>(DefaultFeatures);

interface WithFeaturesProps {
	features: JiraFeatures;
}

export const withJiraFeatures = <T extends WithFeaturesProps>(
	Component: React.ComponentType<T>,
) => {
	return (props: Omit<T, 'features'>) => (
		<JiraFeatureContext.Consumer>
			{(features) => {
				const composedProps = {
					...props,
					features,
				} as T;

				return <Component {...composedProps} />;
			}}
		</JiraFeatureContext.Consumer>
	);
};

export type JiraFeaturesOverrides = Partial<JiraFeatures>;
interface JiraFeaturesProviderProps {
	children: ReactNode;
	features?: JiraFeaturesOverrides;
}

export const JiraFeaturesProvider = ({ features, children }: JiraFeaturesProviderProps) => {
	const abTest = useABTest();
	const enableDateFromFilter = !!abTest?.experimentId?.startsWith(JIRA_DATE_FILTERS_EXPERIMENT_ID);

	const defaultDateFromFilter = BROCCOLI_EXPERIMENTS.find(({ experimentId }) => {
		return experimentId === abTest?.experimentId;
	})?.days;

	const { sites = [] } = useJiraSearchClientContext();
	const { hasSoftwareAccess } = useJiraCurrentUser();

	const value = {
		...DefaultFeatures,
		...features,
		...{ isMultiSite: sites.length > 1 },
		hasSoftwareAccess: features?.hasSoftwareAccess || hasSoftwareAccess || false,
		hasAdvancedRoadmapsAccess: features?.hasAdvancedRoadmapsAccess,
		defaultDateFromFilter: features?.defaultDateFromFilter || defaultDateFromFilter,
		enableDateFromFilter: features?.enableDateFromFilter || enableDateFromFilter,
	};

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const memoedValue = React.useMemo(() => value, [...Object.values(value)]);

	return <JiraFeatureContext.Provider value={memoedValue}>{children}</JiraFeatureContext.Provider>;
};

export const useFeatures = (): JiraFeatures => {
	return useContext(JiraFeatureContext);
};
